// src/context/AuthProvider.tsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { auth } from "../firebase";
import { ensureUserProfile, type UserProfile } from "../services/userService";
import { useApp } from "./AppContext";
import { setUser as setSentryUser, captureError } from "../lib/sentry";

type AuthState = {
  loading: boolean;
  user: User | null;
  profile: UserProfile | null;
  /** Error al cargar/crear el perfil (el login de Firebase salió bien pero no se pudo entrar). */
  profileError: string | null;
  retryProfile: () => void;
  signOutApp: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { tenantId } = useApp();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileAttempt, setProfileAttempt] = useState(0);

  // Suscripción a Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u ?? null);
      if (!u) {
        setProfile(null);
        setLoading(false);
        // Limpiar usuario de Sentry
        setSentryUser(null);
      } else {
        setLoading(true); // esperamos a resolver perfil
      }
    });
    return () => unsub();
  }, []);

  // Resolver/crear perfil al tener user + tenantId
  useEffect(() => {
    let canceled = false;
    async function run() {
      if (!user) return;
      if (!tenantId) return;
      try {
        setLoading(true);
        setProfileError(null);
        const ensured = await ensureUserProfile(tenantId, user);
        if (!canceled) {
          setProfile(ensured);
          // Configurar usuario en Sentry para tracking
          setSentryUser({
            id: user.uid,
            email: user.email ?? undefined,
            name: user.displayName ?? undefined,
          });
        }
      } catch (err) {
        console.error("ensureUserProfile error", err);
        // Antes solo quedaba en la consola: el socio veía el login de nuevo, sin
        // mensaje, y en Sentry no aparecía nada. Ahora se reporta con contexto.
        const code = (err as { code?: string })?.code ?? "unknown";
        captureError(err instanceof Error ? err : new Error(String(err)), {
          context: "ensureUserProfile",
          code,
          uid: user.uid,
          tenantId,
          providers: user.providerData.map((p) => p.providerId).join(","),
        });
        if (!canceled) {
          setProfile(null);
          setProfileError(code);
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    }
    run();
    return () => {
      canceled = true;
    };
  }, [user, tenantId, profileAttempt]);

  // Watchdog: si hay user pero no llega tenantId, no bloquear indefinidamente
  useEffect(() => {
    if (!user || tenantId) return;
    const t = setTimeout(() => setLoading(false), 3500);
    return () => clearTimeout(t);
  }, [user, tenantId]);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      user,
      profile,
      profileError,
      retryProfile: () => setProfileAttempt((n) => n + 1),
      signOutApp: () => signOut(auth),
    }),
    [loading, user, profile, profileError]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
